import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AppHelmet from '../components/CreateACard/Layout/AppHelmet'
import CssBaseline from '@material-ui/core/CssBaseline'
import { MuiThemeProvider } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Layout from '../components/SmallBusiness/OrderFlow/Layout/Layout'
import { smbOrderFlowTheme } from '../components/SmallBusiness/OrderFlow/themeStyles'
import { Redirect, useLocation } from 'react-router-dom'
import DeleteCartItemModal from '../components/SmallBusiness/OrderFlow/Components/DeleteCartItemModal'
import {
    getSmbCartContentsRequest,
    setSmbSelectedProduct,
    smbCheckLoginRequest,
    setSmbCards,
    setApplyAllOverrides,
} from '../actions/smbCart'
import isEmpty from 'lodash/isEmpty'
import has from 'lodash/has'
import isEqual from 'lodash/isEqual'
import {
    getStepByRoute,
    SMB_STATUS,
    STEP_SMB_ORDER_FLOW_CONFIRMATION,
    STEP_SMB_ORDER_FLOW_CUSTOMIZE,
    STEP_SMB_ORDER_FLOW_LANDING,
    STEP_SMB_ORDER_FLOW_ORDER_CANCELLED,
    STEP_SMB_ORDER_FLOW_PLACE_ORDER,
} from '../components/SmallBusiness/SmbConstants'
import GcgCircularProgress from '../components/Loaders/GcgCircularProgress'
import { useQueryParam } from '../hooks/useQueryParam'

export const DELIVERY_STYLE = {
    TOGETHER: 'bulk_ship',
    INDIVIDUAL: 'individual_ship',
    INDIVIDUAL_EMAIL: 'email',
    SPREADSHEET: 'download',
    PHYSICAL_TOGETHER_INFO_TEXT:
        'The cards will be sent to a single location. All cards will still be affixed to a carrier inside the delivered package. Shipping fee is paid per location.',
    PHYSICAL_INDIVIDUAL_INFO_TEXT:
        'The cards will be sent to individual addresses that you specify per card. Shipping fee is paid per card.',
    VIRTUAL_SPREADSHEET_INFO_IMAGE:
        'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto/w_692,h_130/v1622815155/GCG/smb_assets/gcg_spreadsheet_delivery_example_mkq0cw',
    VIRTUAL_SPREADSHEET_INFO_TEXT: (
        <Typography>
            A downloadable spreadsheet will be provided to you with the Card
            Values, Redemption URLs, and an Access Code (Your purchasing email
            address). You will be responsible for providing the Redemption URL
            and Access Code to your card eGift recipients.{' '}
        </Typography>
    ),
    VIRTUAL_INDIVIDUAL_EMAIL_INFO_IMAGE:
        'https://res.cloudinary.com/gift-card-granny/image/upload/f_auto/w_529,h_398/v1622816742/GCG/smb_assets/gcg_example_delivery_email_ouuyld',
    VIRTUAL_INDIVIDUAL_EMAIL_INFO_TEXT: (
        <Typography>
            Gift Card Granny will send an email to the addresses you specify,
            including a clickable URL link to access their eGift code. They will
            use the email address at which they received the eGift to access
            their eGift code.
        </Typography>
    ),
}

export const renderDeliveryStyle = (deliveryStyle) => {
    switch (deliveryStyle) {
        case DELIVERY_STYLE.TOGETHER:
            return 'Bulk delivery, ship to one location'
        case DELIVERY_STYLE.INDIVIDUAL:
            return 'Individual delivery, ship to separate locations'
        case DELIVERY_STYLE.SPREADSHEET:
            return 'Downloadable spreadsheet'
        case DELIVERY_STYLE.INDIVIDUAL_EMAIL:
            return 'Individual delivery, email to recipient'
        default:
            return
    }
}

import { ModuleRegistry } from '@ag-grid-community/core' // @ag-grid-community/core will always be implicitly available
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
ModuleRegistry.registerModules([ClientSideRowModelModule])

const SMBOrderFlow = (props) => {
    const location = useLocation()
    const dispatch = useDispatch()
    const query = useQueryParam()
    const orderWasReordered = query.get('smb_reorder') !== null
    const selectedProduct = useSelector(
        ({ smbCart }) => smbCart.selectedProduct
    )
    const cartContents = useSelector(({ smbCart }) => smbCart.cartContents)
    const cartContentsAreLoading = useSelector(
        ({ smbCart }) => smbCart.cartContentsAreLoading
    )
    const cartItemMarkedForDeletion = useSelector(
        ({ smbCart }) => smbCart.cartItemMarkedForDeletion
    )
    const submittingCobrandForApproval = useSelector(
        (state) => state.smbCart.submittingCobrandForApproval
    )
    const isCheckLoginLoading = useSelector(
        ({ smbCart }) => smbCart.checkLoginIsLoading
    )
    const smbStatus = useSelector((state) => state.smbCart.smbStatus)
    const isSmbStatusLoading = useSelector(
        (state) => state.smbCart.smbStatusIsLoading
    )

    const { children, baseUrl } = props

    // What step in the flow the user is on.
    const stepByRoute = getStepByRoute(location.pathname)

    useEffect(() => {
        // Only store user info (name, email, phone number) if on one of these routes
        if (
            isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_PLACE_ORDER) ||
            isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_CONFIRMATION) ||
            isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_ORDER_CANCELLED)
        ) {
            dispatch(smbCheckLoginRequest({ saveUserInfo: true }))
        } else {
            dispatch(smbCheckLoginRequest())
        }
    }, [dispatch, stepByRoute])

    useEffect(() => {
        dispatch(getSmbCartContentsRequest())
    }, [location.pathname, stepByRoute, dispatch])

    // Reset selectedProduct if on the landing page & it's not already reset
    if (
        isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_LANDING) &&
        !isEmpty(selectedProduct)
    ) {
        dispatch(setSmbSelectedProduct(null))
        dispatch(setSmbCards([]))
        dispatch(setApplyAllOverrides({}))
    }

    if (cartContentsAreLoading) {
        return <GcgCircularProgress />
    }

    if (
        isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_CUSTOMIZE) ||
        submittingCobrandForApproval
    ) {
        if (isCheckLoginLoading || isSmbStatusLoading) {
            return <GcgCircularProgress />
        }
    }
    if (
        (isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_CUSTOMIZE) &&
            isEmpty(selectedProduct)) ||
        (isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_PLACE_ORDER) &&
            !orderWasReordered &&
            (isEmpty(cartContents) ||
                (has(cartContents, 'cart_items') &&
                    isEmpty(cartContents.cart_items))))
    ) {
        return <Redirect to={STEP_SMB_ORDER_FLOW_LANDING.route} />
    }

    if (
        !isEqual(stepByRoute, STEP_SMB_ORDER_FLOW_LANDING) &&
        smbStatus.smb_status === SMB_STATUS.REJECTED
    ) {
        return <Redirect to={STEP_SMB_ORDER_FLOW_LANDING.route} />
    }

    return (
        <>
            <MuiThemeProvider theme={smbOrderFlowTheme}>
                <AppHelmet
                    pageTitle={stepByRoute.pageTitle}
                    pageDescription={stepByRoute.pageDescription}
                />
                <CssBaseline />
                <Layout
                    step={stepByRoute}
                    baseUrl={baseUrl}
                    brandLogoUrl={props.brandLogoUrl}
                    footerContent={props.footerContent}
                >
                    {children}
                    {!isEmpty(cartItemMarkedForDeletion.item) && (
                        <DeleteCartItemModal
                            open={cartItemMarkedForDeletion.openModal}
                            item={cartItemMarkedForDeletion.item}
                            cartId={cartItemMarkedForDeletion.cartId}
                        />
                    )}
                </Layout>
            </MuiThemeProvider>
        </>
    )
}

export default SMBOrderFlow
